/**
 * The hand builder — `/analyzer/new`. An INTERACTIVE TABLE where the ACTION
 * drives the reveal: the seats are laid out up front on an oval felt (tap a
 * seat → set Hero / add villain, assign hole cards, set the effective stack),
 * then the action is entered per street, GUIDED by the engine. Each reachable
 * street section shows its (editable) board cards + whose turn it is + only the
 * legal buttons, advancing as the user taps.
 *
 * Progressive street reveal (the model the user found legible): a postflop
 * street only OPENS once the prior street's round closed with ≥2 players live
 * (`reachableStreets`). When a street becomes reachable we materialise its
 * street object (board + actions) in a `useEffect`; when an earlier edit makes a
 * later street unreachable, we drop the now-stale street objects. Each postflop
 * section places its own board (flop = 3 cards, turn = 1, river = 1) and gates
 * its keypad until the board is complete. `endedOn` is derived, never picked.
 *
 * State is one `BuiltHand` draft mutated immutably; hero may be unplaced and
 * hole-card slots empty until enough is set. Engine calls are guarded until
 * hero is placed. `validateBuiltHand` is the final Save gate — disabled + inline
 * errors until it returns []. Save flow (session-aware) is preserved exactly.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import type {
  BuiltHand,
  BuiltHandAction,
  BuiltHandStreets,
  HandActionKind,
} from "@/shared/models";
import { saveHand } from "@/shared/api";
import { handAnalysisRoute } from "@/shared/chatRoute";
import { Button } from "@/shared/components/Button";
import { BackLink } from "@/shared/components/BackLink";
import { NavIcons, Sparkles } from "@/shared/components/icons";
import { PageContainer } from "@/shared/components/layout/PageContainer";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import type { Position } from "@/features/chat/lib/seatLayout";

import {
  cardsDisabledFor,
  deriveEndedOn,
  emptyHand,
  isValidCard,
  reachableStreets,
  usedCards,
  validateBuiltHand,
} from "../lib/builtHand";
import {
  actorForSeat,
  nextDecision,
  SMALL_BLIND,
  BIG_BLIND,
  type StreetKey,
} from "../lib/handTableEngine";
import { HandTable, type SeatModel } from "../components/HandTable";
import {
  StreetActions,
  type StreetBoardSlot,
} from "../components/StreetActions";
import { HandReplayPreview } from "../components/HandReplayPreview";

const MAX_VILLAINS = 3;

const STREET_LABEL: Record<StreetKey, string> = {
  preflop: "Preflop",
  flop: "Flop",
  turn: "Turn",
  river: "River",
};

const inputClass =
  "w-full rounded-lg border border-border bg-bg px-3 py-2 text-text placeholder:text-text-muted/60 focus:outline-none focus:border-accent/50 transition";

/** A draft starts with NO hero placed and no villains — a blank felt. The user
 *  taps seats to populate it. We re-use emptyHand's shape but clear the seats. */
function draftHand(): BuiltHand {
  return {
    ...emptyHand(),
    heroPosition: "", // unplaced sentinel; validator flags until set
    heroCards: ["", ""],
    villains: [],
  };
}

export function HandBuilder() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // When launched from a logged session (the tracker doorway), the built hand
  // is attached to it and we return to that session afterwards.
  const sessionId = searchParams.get("session_id") ?? undefined;
  const [hand, setHand] = useState<BuiltHand>(draftHand);
  const [title, setTitle] = useState("");

  const errors = useMemo(() => validateBuiltHand(hand), [hand]);
  const valid = errors.length === 0;
  const used = useMemo(() => usedCards(hand), [hand]);

  const heroPlaced = !!hand.heroPosition;
  const villainCount = hand.villains.length;
  const canAddVillain = villainCount < MAX_VILLAINS;

  const saveMutation = useMutation({
    mutationFn: () => saveHand(hand, title.trim() || undefined, sessionId),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["hands"] });
      navigate(sessionId ? `/tracker/${sessionId}` : handAnalysisRoute(saved.id));
    },
  });

  // ---- Street reveal: the ACTION opens streets, not a board count ----------
  // `reachableStreets` reads the engine to decide which streets the entered
  // action has opened. We materialise/drop the street OBJECTS to match in an
  // effect (never during render) so board + actions have somewhere to live.
  const reachable = useMemo(
    () => (heroPlaced && villainCount > 0 ? reachableStreets(hand) : ["preflop" as StreetKey]),
    [hand, heroPlaced, villainCount],
  );
  const reachableKey = reachable.join(",");

  // Materialise/drop street objects when the reachable SET changes. This MUST
  // run in an effect (not render): it derives one piece of `hand` from another
  // (the entered action → which street objects exist), which can't be done
  // during render without mutating state mid-render. `syncStreetObjects` returns
  // the same object when nothing changed, so the cascade settles in one pass.
  // Keyed on the reachable SET (stringified) — only re-sync when it changes.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHand((h) => syncStreetObjects(h, reachable));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachableKey]);

  const disabledForCard = (current: string | null) =>
    cardsDisabledFor(used, current);

  // ---- Seats ---------------------------------------------------------------
  function setHero(position: Position) {
    setHand((h) => ({ ...h, heroPosition: position, heroCards: ["", ""] }));
  }
  function addVillain(position: Position) {
    setHand((h) => {
      if (h.villains.length >= MAX_VILLAINS) return h;
      return { ...h, villains: [...h.villains, { position, cards: null }] };
    });
  }
  function removeSeat(position: Position) {
    setHand((h) => {
      if (h.heroPosition === position) {
        return { ...h, heroPosition: "", heroCards: ["", ""] };
      }
      return {
        ...h,
        villains: h.villains.filter((v) => v.position !== position),
      };
    });
  }
  function setSeatCard(position: Position, idx: 0 | 1, token: string | null) {
    setHand((h) => {
      if (h.heroPosition === position) {
        const heroCards: [string, string] = [...h.heroCards];
        heroCards[idx] = token ?? "";
        return { ...h, heroCards };
      }
      return {
        ...h,
        villains: h.villains.map((v) => {
          if (v.position !== position) return v;
          const cards: [string, string] = v.cards ? [...v.cards] : ["", ""];
          cards[idx] = token ?? "";
          return { ...v, cards };
        }),
      };
    });
  }
  function toggleVillainShown(position: Position, shown: boolean) {
    setHand((h) => ({
      ...h,
      villains: h.villains.map((v) =>
        v.position === position ? { ...v, cards: shown ? ["", ""] : null } : v,
      ),
    }));
  }
  function setStack(value: string) {
    const n = parseFloat(value);
    setHand((h) => ({ ...h, effectiveStackBb: Number.isNaN(n) ? 0 : n }));
  }

  // ---- Board card setters (per street) -------------------------------------
  function setFlopCard(idx: 0 | 1 | 2, token: string | null) {
    setHand((h) => {
      const flop = h.streets.flop;
      if (!flop) return h;
      const board: [string, string, string] = [...flop.board];
      board[idx] = token ?? "";
      const next = {
        ...h,
        streets: { ...h.streets, flop: { ...flop, board } },
      };
      return { ...next, endedOn: deriveEndedOn(next) };
    });
  }
  function setTurnCard(token: string | null) {
    setHand((h) => {
      const turn = h.streets.turn;
      if (!turn) return h;
      const next = {
        ...h,
        streets: { ...h.streets, turn: { ...turn, card: token ?? "" } },
      };
      return { ...next, endedOn: deriveEndedOn(next) };
    });
  }
  function setRiverCard(token: string | null) {
    setHand((h) => {
      const river = h.streets.river;
      if (!river) return h;
      const next = {
        ...h,
        streets: { ...h.streets, river: { ...river, card: token ?? "" } },
      };
      return { ...next, endedOn: deriveEndedOn(next) };
    });
  }

  // The seat models the table renders. Blinds are ALWAYS posted on SB/BB even
  // when no tracked player sits there (dead blinds), so the 1bb is never
  // forgotten.
  const seats: SeatModel[] = (["UTG", "UTG1", "MP", "LJ", "HJ", "CO", "BTN", "SB", "BB"] as Position[]).map(
    (position) => {
      const isHero = hand.heroPosition === position;
      const villain = hand.villains.find((v) => v.position === position);
      const role: SeatModel["role"] = isHero ? "hero" : villain ? "villain" : null;
      const cards = isHero
        ? hand.heroCards
        : (villain?.cards ?? null);
      const blind =
        position === "SB" ? SMALL_BLIND : position === "BB" ? BIG_BLIND : null;
      return {
        position,
        role,
        cards: role ? (cards as [string, string]) : null,
        blind,
        toAct: false, // filled in below per active street
      };
    },
  );

  // Community cards revealed so far — read-only display on the felt.
  const revealedBoard: string[] = useMemo(() => {
    const out: string[] = [];
    const { flop, turn, river } = hand.streets;
    if (flop) flop.board.forEach((c) => isValidCard(c) && out.push(c));
    if (turn && isValidCard(turn.card)) out.push(turn.card);
    if (river && isValidCard(river.card)) out.push(river.card);
    return out;
  }, [hand.streets]);

  // ---- Action entry --------------------------------------------------------
  function appendAction(street: StreetKey, action: HandActionKind, amount?: number) {
    setHand((h) => {
      const next = mutateStreetActions(h, street, (acts) => {
        // The acting seat is whatever the engine says is next. Store the ACTOR
        // label ("Hero" for the hero seat, the seat itself for villains) — the
        // wire contract + validator require actor ∈ {"Hero"} ∪ villain seats, so
        // storing the raw hero seat (e.g. "BTN") would 422 on save.
        const seat = nextSeatToAct(h, street);
        if (!seat) return acts;
        const entry: BuiltHandAction = { actor: actorForSeat(h, seat), action };
        if (amount !== undefined) entry.amount = amount;
        return [...acts, entry];
      });
      return { ...next, endedOn: deriveEndedOn(next) };
    });
  }
  function undoAction(street: StreetKey) {
    setHand((h) => {
      const next = mutateStreetActions(h, street, (acts) => acts.slice(0, -1));
      return { ...next, endedOn: deriveEndedOn(next) };
    });
  }

  // Build the per-street editable board slots + completeness flag.
  function flopSlots(): StreetBoardSlot[] {
    const board = hand.streets.flop?.board ?? ["", "", ""];
    return ([0, 1, 2] as const).map((i) => ({
      token: board[i] || null,
      disabled: disabledForCard(board[i] || null),
      onPick: (t) => setFlopCard(i, t),
      onClear: () => setFlopCard(i, null),
    }));
  }
  function turnSlot(): StreetBoardSlot[] {
    const card = hand.streets.turn?.card || null;
    return [
      {
        token: card,
        disabled: disabledForCard(card),
        onPick: (t) => setTurnCard(t),
        onClear: () => setTurnCard(null),
      },
    ];
  }
  function riverSlot(): StreetBoardSlot[] {
    const card = hand.streets.river?.card || null;
    return [
      {
        token: card,
        disabled: disabledForCard(card),
        onPick: (t) => setRiverCard(t),
        onClear: () => setRiverCard(null),
      },
    ];
  }
  function boardFor(street: StreetKey): {
    slots: StreetBoardSlot[];
    complete: boolean;
  } {
    switch (street) {
      case "preflop":
        return { slots: [], complete: true };
      case "flop": {
        const slots = flopSlots();
        return { slots, complete: slots.every((s) => isValidCard(s.token)) };
      }
      case "turn": {
        const slots = turnSlot();
        return { slots, complete: isValidCard(slots[0]!.token) };
      }
      case "river": {
        const slots = riverSlot();
        return { slots, complete: isValidCard(slots[0]!.token) };
      }
    }
  }

  // Highlight the seat to act on the SHALLOWEST still-open reachable street
  // whose board is complete. (Plain computation — engine calls inline.)
  const guidedSeat = computeGuidedSeat(hand, heroPlaced, reachable, (s) =>
    boardFor(s).complete,
  );

  const seatsWithGuidance: SeatModel[] = seats.map((s) =>
    guidedSeat && s.position === guidedSeat.seat ? { ...s, toAct: true } : s,
  );

  return (
    <PageContainer width="default">
      <BackLink
        to={sessionId ? `/tracker/${sessionId}` : "/analyzer"}
        label={sessionId ? "Session" : "Hand Analyzer"}
      />
      <PageHeader
        Icon={NavIcons.analyzer}
        title="Build a hand"
        subtitle={
          sessionId
            ? "Tap the table to lay out a hand from this session, then enter the action."
            : "Tap the table to lay out the hand, then enter the action — your coach grades the line."
        }
      />

      {/* The interactive table — seats + revealed community cards (read-only) */}
      <HandTable
        seats={seatsWithGuidance}
        revealedBoard={revealedBoard}
        effectiveStackBb={hand.effectiveStackBb}
        heroPlaced={heroPlaced}
        canAddVillain={canAddVillain}
        usedCards={used}
        onSetHero={setHero}
        onAddVillain={addVillain}
        onRemoveSeat={removeSeat}
        onSetCard={setSeatCard}
        onToggleVillainShown={toggleVillainShown}
        onStackChange={setStack}
      />

      {!heroPlaced && (
        <p className="text-center text-sm text-text-muted">
          Tap a seat to set Hero, then add at least one villain to start entering
          action.
        </p>
      )}

      {/* Per-street action — only once hero (+ a villain) is on the felt. Each
          reachable street reveals as the action opens it; postflop streets
          place their own board before the keypad unlocks. */}
      {heroPlaced && hand.villains.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-text">Action</h2>
          <div className="space-y-3">
            {reachable.map((key) => {
              const { slots, complete } = boardFor(key);
              return (
                <StreetActions
                  key={key}
                  hand={hand}
                  street={key}
                  label={STREET_LABEL[key]}
                  boardSlots={slots}
                  boardComplete={complete}
                  onAppend={(action, amount) => appendAction(key, action, amount)}
                  onUndo={() => undoAction(key)}
                />
              );
            })}
          </div>
          <p className="text-xs text-text-muted">
            Hand ends on:{" "}
            <span className="font-medium text-text capitalize">{hand.endedOn}</span>
            {" "}— derived from the action you entered.
          </p>
        </section>
      )}

      {/* For the coach */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-text">For the coach</h2>
        <label className="block">
          <span className="text-sm font-medium text-text">Your question — optional</span>
          <textarea
            value={hand.notes ?? ""}
            onChange={(e) =>
              setHand((h) => ({ ...h, notes: e.target.value.slice(0, 2000) || undefined }))
            }
            rows={3}
            placeholder="What were you unsure about? e.g. “Was my turn check-raise too thin?”"
            className={`mt-1.5 ${inputClass} resize-none`}
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-text">Title — optional</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 80))}
            placeholder="AKs vs the BTN 3-bettor"
            className={`mt-1.5 ${inputClass}`}
          />
        </label>
      </section>

      {/* Preview */}
      {valid && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-text">Preview</h2>
          <HandReplayPreview hand={hand} />
        </section>
      )}

      {/* Errors */}
      {!valid && heroPlaced && (
        <div className="rounded-2xl border border-danger/30 bg-danger/5 p-4">
          <p className="text-sm font-medium text-text">A few things to finish:</p>
          <ul className="mt-2 space-y-1">
            {errors.map((e, i) => (
              <li key={i} className="flex gap-2 text-sm text-text-muted">
                <span className="text-danger">•</span>
                {e}
              </li>
            ))}
          </ul>
        </div>
      )}

      {saveMutation.isError && (
        <p className="text-sm text-danger">Couldn't save — try again.</p>
      )}

      {/* CTA — building/saving is free; the coach grade is paid, walled
       *  server-side (ChatView pops the modal), so a direct navigate is fine.
       *  Label is session-aware. */}
      <Button
        variant="primary"
        size="md"
        fullWidth
        disabled={!valid || saveMutation.isPending}
        onClick={() => saveMutation.mutate()}
      >
        <Sparkles size={18} strokeWidth={2} />
        {saveMutation.isPending
          ? "Saving…"
          : sessionId
            ? "Save to session"
            : "Save & analyze"}
      </Button>
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// Pure-ish draft helpers (operate on a BuiltHand, return a new one)
// ---------------------------------------------------------------------------

/** Materialise/drop street OBJECTS so the reachable set has somewhere to store
 *  board + actions. Creating a fresh street seeds an empty board; dropping a
 *  now-unreachable street removes its stale data. Preflop always survives.
 *  Returns the same object when nothing changed (so the effect is a no-op). */
function syncStreetObjects(h: BuiltHand, reachable: StreetKey[]): BuiltHand {
  const want = new Set(reachable);
  const streets: BuiltHandStreets = { preflop: h.streets.preflop };
  let changed = false;

  if (want.has("flop")) {
    streets.flop = h.streets.flop ?? { board: ["", "", ""], actions: [] };
    if (!h.streets.flop) changed = true;
  } else if (h.streets.flop) {
    changed = true;
  }

  if (want.has("turn")) {
    streets.turn = h.streets.turn ?? { card: "", actions: [] };
    if (!h.streets.turn) changed = true;
  } else if (h.streets.turn) {
    changed = true;
  }

  if (want.has("river")) {
    streets.river = h.streets.river ?? { card: "", actions: [] };
    if (!h.streets.river) changed = true;
  } else if (h.streets.river) {
    changed = true;
  }

  if (!changed) return h;
  const next = { ...h, streets };
  return { ...next, endedOn: deriveEndedOn(next) };
}

/** Replace one street's actions immutably. */
function mutateStreetActions(
  h: BuiltHand,
  key: StreetKey,
  fn: (actions: BuiltHandAction[]) => BuiltHandAction[],
): BuiltHand {
  const street = h.streets[key];
  if (!street) return h;
  return {
    ...h,
    streets: {
      ...h.streets,
      [key]: { ...street, actions: fn(street.actions) },
    },
  };
}

/** The seat the engine says acts next on `street`, or null. The engine is the
 *  single source of action order; we only guard the unplaced-hero case. */
function nextSeatToAct(h: BuiltHand, street: StreetKey): string | null {
  if (!h.heroPosition) return null;
  return nextDecision(h, street)?.seat ?? null;
}

/** The shallowest still-open reachable street (with a complete board) + the
 *  seat to act there — the one seat we highlight on the felt. Null once every
 *  reachable street's round is closed (or its board isn't placed yet). */
function computeGuidedSeat(
  hand: BuiltHand,
  heroPlaced: boolean,
  reachable: StreetKey[],
  boardComplete: (s: StreetKey) => boolean,
): { street: StreetKey; seat: string } | null {
  if (!heroPlaced) return null;
  for (const key of reachable) {
    if (!boardComplete(key)) return null; // earlier street's board still pending
    const seat = nextSeatToAct(hand, key);
    if (seat) return { street: key, seat };
  }
  return null;
}
